import { Location, TitleCasePipe } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { MaintenanceProvider } from "../../maintenance-provider/maintenance-provider.types";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { ContractService } from "../contract.service";
import { Contract, ContractStatus } from "../contract.type";
import { CreateContractService } from "./create-contract.service";

@Component({
  selector: "app-create-contract",
  templateUrl: "./create-contract.component.html",
  styleUrls: ["./create-contract.component.css"],
  providers: [CreateContractService, TitleCasePipe],
})
export class CreateContractComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  currentUser: UserInfo;
  questions: QuestionBase<string | boolean | number>[];
  maintenanceProviders: MaintenanceProvider[];
  partner: Partner;
  operation: Operation;

  constructor(
    public location: Location,
    private router: Router,
    private loader: LoadingService,
    private contractService: ContractService,
    private notificationService: NotificationService,
    private questionsService: CreateContractService,
    private authenticationService: AuthenticationService,
    private partnerService: PartnerService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.operation = Operation.create;
  }

  ngOnInit(): void {
    let hideReseller = true;

    if (this.currentUser.isPartner()) {
      hideReseller = !this.currentUser.partner.isResellerNeeded;
    }
    if (this.currentUser.isCompany()) {
      hideReseller = true;
    }

    this.loader.show();

    this.questionsService
      .generateQuestions({
        ...(this.currentUser.isCompany() && {
          companyValue: this.currentUser.selectedCompanyId || this.currentUser.companyId,
          partnerSkip: true,
          companySkip: true,
          ...(this.currentUser.hasManyCompanies() && {
            companySkip: false,
            partnerValue: this.currentUser.company.partnerId,
            companyEdit: false,
          }),
        }),
        ...(this.currentUser.isPartner() && {
          partnerValue: this.currentUser.partnerId,
          partnerSkip: true,
        }),
        currentUser: this.currentUser,
        hideReseller: hideReseller,
        operation: this.operation,
      })
      .subscribe((qs) => {
        this.questions = qs;
        this.loader.hide();
      });
  }

  goBack(): void {
    this.location.back();
  }

  async onChange(event: AnyObject): Promise<void> {
    if (event.key === "endDate" || event.key === "startDate") {
      if (event.key === "startDate") {
        this.dynamicForm.getQuestion("endDate").minDate = event.value;
      }

      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.generateContractStatusQuestion(
          "status",
          "Status",
          8,
          this.dynamicForm.form.get("startDate").value,
          this.dynamicForm.form.get("endDate").value,
        ),
      );
    }

    if (event.source?.id === "status") {
      const statusValue = event.value;
      const startDateSelected = this.dynamicForm.form.get("startDate").value;

      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.createDateQuestion({ statusValue, startDateSelected }),
      );
      if (statusValue === ContractStatus.pending) {
        this.dynamicForm.form.get("startDate").markAllAsTouched();
      }
    }
    if (event.source?.id === "partnerId") {
      const partnerId = event.value;
      this.partner = await this.getPartner(partnerId);
      const resellerField = this.dynamicForm.getQuestion("reseller");
      if (resellerField) {
        resellerField.hide = !this.partner.isResellerNeeded;
      }
    }
  }

  onSubmit(values: Contract): void {
    for (const propName in values) {
      if (values[propName] === "") {
        delete values[propName];
      }
    }
    this.contractService
      .createContract({
        ...values,
      })
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (contract: Contract) => {
          this.notificationService.success("Contract successfully created");
          this.router.navigate([`/contracts/${contract.id}`]);
        },
        (error) => {
          this.notificationService.error(error || "Could not create contract");
        },
      );
  }

  private async getPartner(id: string) {
    return this.partnerService.getPartner(id).toPromise();
  }
}
